<script setup lang="ts">
import { StudyGroupModel, UserModel, type IStudyGroupPermissions } from '@/models';
import { useFormStore } from '@/stores/forms';
import { cloneDeep } from 'lodash';
import { computed } from 'vue';


const props = defineProps({
  user: {
    type: UserModel,
    required: true
  }
});

const formsStore = useFormStore();
const user = computed(() => props.user);

const combinedPermissions = computed(() => {
  const userPerms = cloneDeep(user.value.permissions);
  for (const group of user.value.groups ?? []) {
    if (group.permissions) {
      for (const key of Object.keys(group.permissions)) {
        const permissionKey = key as keyof IStudyGroupPermissions;
        const groupPermissionValue = group.permissions[permissionKey];

        if (groupPermissionValue) {
          const userPermissionValue = userPerms[permissionKey];

          if (Array.isArray(groupPermissionValue)) {
            const existingPermissions = Array.isArray(userPermissionValue) ? userPermissionValue : [];
            const combined = [...existingPermissions, ...groupPermissionValue];
            // Use a Set to remove duplicates and convert back to an array
            (userPerms[permissionKey] as string[]) = [...new Set(combined)];
          } else if (groupPermissionValue === true && !Array.isArray(userPermissionValue)) {
            userPerms[permissionKey] = true;
          }
        }
      }
    }
  }
  return userPerms;
});

const permissionLabels = [
  'View Forms',
  'Add/Edit Entries',
  'View Entries',
  'Export Data',
  'Manage Study',
  'Edit Cases'
];

const getPermissionKey = (label: string): string[] => {
  switch (label) {
    case 'View Forms':
      return ['forms'];
    case 'Add/Edit Entries':
      return ['editForms', 'addEntries'];
    case 'View Entries':
      return ['viewEntries'];
    case 'Export Data':
      return ['export'];
    case 'Manage Study':
      return ['manageInvest'];
    case 'Edit Cases':
      return ['editCases'];
    default:
      return [];
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getPermissionValue = (permissionSet: any, label: string): boolean | number => {
  if (!permissionSet) {
    return false;
  }
  const keys = getPermissionKey(label);
  for (const key of keys) {
    const value = permissionSet[key];
    // console.log('Checking permission:', key, 'Value:', value);
    if (Array.isArray(value)) {
      return value.length;
    }
    if (value === true) {
      return true;
    }
  }
  // Check if any of the keys are true (for combined permissions)
  if (keys.some(key => permissionSet[key])) {
    return true;
  }

  return false;
};

const getPermissionDisplay = (permissionSet: unknown, label: string): string | boolean => {
  const value = getPermissionValue(permissionSet, label);
  if (typeof value === 'number' && value > 0 && value < formsStore.formList.length) {
    let noun = label.replace('View ', '').replace('Edit ', '');
    if (noun == 'Add/Entries') {
      noun = 'Forms';
    }
    return `${value} ${noun}`;
  }
  return value as boolean;
};

const emits = defineEmits(['onGroupEdit', 'onUserEdit']);

const openGroup = (group: StudyGroupModel) => {
  emits('onGroupEdit', group);
};

const openUserPermissions = (userModel: UserModel) => {
  emits('onUserEdit', userModel);
};
</script>

<template>
    <div class="expand-root">
        <table class="permissions-table">
            <thead>
                <tr>
                    <th>Permission</th>
                    <th class="combined-col">Current permissions</th>
                    <th class="th-edit" @click="openUserPermissions(user)" v-ripple>
                        <span>User permissions</span>
                        <FontAwesomeIcon :icon="['far', 'pencil']" class="edit" />
                    </th>
                    <th v-for="group in user.groups" :key="group.identifier" v-ripple>
                        <div class="th-edit" @click="openGroup(group)" v-ripple>
                            <span>{{ group.name }}</span>
                            <FontAwesomeIcon :icon="['far', 'pencil']" class="edit" />
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="label in permissionLabels" :key="label">
                    <td>{{ label }}</td>
                    <td class="combined-col">
                        <span v-if="typeof getPermissionDisplay(combinedPermissions, label) === 'string'">
                            {{ getPermissionDisplay(combinedPermissions, label) }}
                        </span>
                        <template v-else>
                            <FontAwesomeIcon v-if="getPermissionDisplay(combinedPermissions, label)"
                                :icon="['fas', 'check']"
                                class="permission-yes" />
                            <FontAwesomeIcon v-else :icon="['fas', 'times']" class="permission-no" />
                        </template>
                    </td>
                    <td>
                        <span v-if="typeof getPermissionDisplay(user.permissions, label) === 'string'">
                            {{ getPermissionDisplay(user.permissions, label) }}
                        </span>
                        <template v-else>
                            <FontAwesomeIcon v-if="getPermissionDisplay(user.permissions, label)" :icon="['fas', 'check']" class="permission-yes" />
                            <FontAwesomeIcon v-else :icon="['fas', 'times']" class="permission-no" />
                        </template>
                    </td>
                    <td v-for="group in user.groups" :key="group.identifier">
                        <span v-if="typeof getPermissionDisplay(group.permissions, label) === 'string'">
                            {{ getPermissionDisplay(group.permissions, label) }}
                        </span>
                        <template v-else>
                            <FontAwesomeIcon v-if="getPermissionDisplay(group.permissions, label)" :icon="['fas', 'check']" class="permission-yes" />
                            <FontAwesomeIcon v-else :icon="['fas', 'times']" class="permission-no" />
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style scoped lang="scss">
    .expand-root {
        display: flex;
        gap: 16px;
        padding: 16px;
    }
    .groups-permissions {
        display: flex;
        gap: 16px;
        padding: 16px;
    }
    .th-edit {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        .edit {
            color: #007bff;
            &:hover {
                color: #0056b3;
            }
            margin-left: 16px;
        }
    }
    .permissions-table {
        border-collapse: collapse;
        th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
        }
        th {
            background-color: #f2f2f2;
        }
        td:nth-child(n+2):nth-child(-n+5) {
            text-align: center;
        }
    }
    .combined-col {
      /* Increase width and change color to make it stand out */
      border-right: 2px solid #4a5568 !important; 
    }
    .permission-yes {
        color: green;
    }
    .permission-no {
        color: red;
    }
</style>
